import { useState } from 'react';
import { apiClient } from '../client';

/**
 * Stripe account status response
 */
export interface StripeAccountStatus {
  connected: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  fullyOnboarded?: boolean;
}

/**
 * Stripe onboarding response
 */
export interface StripeOnboardingResponse {
  url: string;
  accountId: string;
  existing: boolean;
}

/**
 * Stripe dashboard response
 */
export interface StripeDashboardResponse {
  url: string;
}

/**
 * Hook for starting Stripe Connect onboarding.
 * Creates a connected account and returns onboarding URL.
 *
 * @returns Object with onboard function, loading state, and error
 */
export function useStripeOnboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const onboard = async (): Promise<StripeOnboardingResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<StripeOnboardingResponse>(
        '/therapist/stripe/onboard'
      );

      return response.data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to start onboarding');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { onboard, loading, error };
}

/**
 * Hook for fetching Stripe account status.
 * Returns current status of connected account.
 *
 * @returns Object with getStatus function, loading state, and error
 */
export function useStripeStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<StripeAccountStatus | null>(null);

  const getStatus = async (): Promise<StripeAccountStatus> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<StripeAccountStatus>(
        '/therapist/stripe/status'
      );

      setStatus(response.data);
      return response.data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get status');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => getStatus();

  return { getStatus, refresh, status, loading, error };
}

/**
 * Hook for getting Stripe Express dashboard link.
 * Returns URL to Stripe dashboard for connected account.
 *
 * @returns Object with getDashboard function, loading state, and error
 */
export function useStripeDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getDashboard = async (): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<StripeDashboardResponse>(
        '/therapist/stripe/dashboard'
      );

      return response.data.url;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get dashboard link');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { getDashboard, loading, error };
}
